import React, { useEffect, useRef, useState } from "react";
import { Nav, Footer, About as AboutSection } from "@/pages/Landing";
import { Globe2, Building2, UsersRound } from "lucide-react";

/**
 * Animated count-up that starts once the node scrolls into view.
 * Uses requestAnimationFrame with an ease-out curve for a premium feel,
 * and IntersectionObserver so the animation fires exactly once, right when
 * the user can actually see the number.
 */
function useCountUp(target, { duration = 1400, when = true } = {}) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!when || started) return;
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStarted(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [when, started]);

  useEffect(() => {
    if (!started) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic — fast start, gentle settle
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, target, duration]);

  return [ref, value, started];
}

function StatChip({ icon: Icon, target, label, delay = 0 }) {
  const [ref, value, started] = useCountUp(target, { duration: 1400 + delay });
  return (
    <div
      ref={ref}
      className="flex items-center gap-3 sm:gap-4 group transition-all duration-500"
      style={{
        opacity: started ? 1 : 0,
        transform: started ? "translateY(0)" : "translateY(12px)",
        transitionDelay: `${delay}ms`,
      }}
      data-testid={`about-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="w-10 h-14 sm:w-11 sm:h-16 rounded-full bg-[#F4E8E9] flex items-center justify-center shrink-0 ring-1 ring-inset ring-[#6B0F1A]/10 group-hover:scale-105 transition-transform duration-300">
        <Icon className="w-5 h-5 text-[#6B0F1A]" strokeWidth={1.8} />
      </div>
      <div>
        <div className="font-display font-extrabold text-2xl sm:text-3xl text-[#1F1F1F] leading-none tabular-nums" data-testid={`about-stat-value-${target}`}>
          {value}<span className="text-[#6B0F1A]">+</span>
        </div>
        <div className="text-stone-500 text-xs sm:text-[13px] mt-1.5 tracking-wide">{label}</div>
      </div>
    </div>
  );
}

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Nav />

      {/* Hero — matches traderepubliq.com/about tagline placed at the very top */}
      <section
        className="relative pt-28 pb-16 md:pt-32 md:pb-20 bg-gradient-to-b from-[#FBFAF9] to-white border-b border-stone-100 overflow-hidden"
        data-testid="about-hero"
      >
        {/* Subtle animated background grid for depth — same tone as the stat pills */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, #6B0F1A 1px, transparent 1px), linear-gradient(to bottom, #6B0F1A 1px, transparent 1px)",
            backgroundSize: "72px 72px",
          }}
        />

        <div className="relative max-w-6xl mx-auto px-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#6B0F1A] mb-4" data-testid="about-hero-label">
            About TradeRepubliq
          </div>
          <h1
            className="font-display font-extrabold tracking-tight text-[#1F1F1F] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl"
            data-testid="about-hero-title"
          >
            Execution-Led<br />Trade Partner
          </h1>
          <p className="mt-6 max-w-2xl text-stone-600 text-base md:text-lg leading-relaxed" data-testid="about-hero-subtitle">
            We are a counterparty-grade trade operator delivering essential agricultural resources from origin to market on trusted supply-chain rails.
          </p>

          {/* Animated stat strip — count-up triggers when scrolled into view */}
          <div className="mt-10 sm:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-10 max-w-3xl" data-testid="about-hero-stats">
            <StatChip icon={Globe2}    target={15}  label="Origin Countries" delay={0}   />
            <StatChip icon={Building2} target={40}  label="Markets Served"   delay={150} />
            <StatChip icon={UsersRound} target={200} label="Farm Partners"    delay={300} />
          </div>
        </div>
      </section>

      <AboutSection />
      <Footer />
    </div>
  );
}
